// ============================================================================
// STATISTIQUES › VENTES › REMISES — combien la période a remisé, où vont les remises, qui remise quoi, et comment ça
// évolue. Réf. maquette du back-office (« Remises ») et les décisions S1, S2 de son bilan.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : totaux, parts, écarts, colonnes des vendeurs et seaux viennent du
//    serveur (`sales/stats-discounts.ts`). Ce qui se calcule ici est de la GÉOMÉTRIE (largeurs, hauteurs, teintes).
// ⚠️ LA MAQUETTE SIMULE SES PARTS PAR DES PROPORTIONS, PAS NOUS : chaque montant est celui que la caisse a scellé, et ce
//    qui ne se répartit pas se dit (« Non détaillé », prix libres comptés sans montant).
// ============================================================================
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Trans, useChartTip } from '@orgazone/ui-web';
import type { SalesDiscountPart, SalesDiscountTotals, SalesDiscountsView } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { dTx, eur0, pc, pctOf } from './stats-format';
import {
  axisLabel, bucketLabels, CARD, CARD_ROW, CardHead, chip, EMPTY, KPI_ROW, KPI_TILE, KpiCard, KpiLabel, MONO_LABEL, niceTop, TONES,
} from './stats-resume';

/** Les teintes des motifs, dans l'ordre de leur poids — celles de la maquette. */
const REASON_COLORS = ['var(--violet)', 'var(--teal-500)', 'var(--rose)', 'var(--teal-800)', 'var(--violet-fg)', 'var(--ink-500)'];

/** Les parts qui ne sont pas des motifs : leur nom, leur phrase et leur teinte. */
const PARTS: Record<SalesDiscountPart, { label: Translatable; sub: Translatable; color: string; neutral?: boolean }> = {
  client: { label: key('Remise client'), sub: key('Remise permanente de la fiche du client'), color: 'var(--teal-600)' },
  loyalty: { label: key('Fidélité'), sub: key('Cagnotte utilisée en caisse'), color: 'var(--rose)' },
  promo: { label: key('Promotions & soldes'), sub: key('Automatiques : prix barrés, démarques, promotions'), color: 'var(--blue)', neutral: true },
  withoutReason: { label: key('Sans motif'), sub: key('Gestes saisis en caisse sans motif'), color: 'var(--amber)' },
  undetailed: { label: key('Non détaillé'), sub: key('Ventes d’avant le détail des remises : un total, sans parts'), color: 'var(--ink-300)' },
};
/** L'ordre des parts sous les motifs. Promotions et gestes sans motif restent toujours, même à zéro (maquette). */
const PART_ORDER: readonly SalesDiscountPart[] = ['client', 'loyalty', 'promo', 'withoutReason', 'undetailed'];
const ALWAYS: ReadonlySet<SalesDiscountPart> = new Set(['promo', 'withoutReason']);

/** La teinte d'un motif, par son rang dans la liste. */
const reasonColor = (i: number): string => REASON_COLORS[i % REASON_COLORS.length]!;
/** Ce qu'on affiche d'un motif : son libellé, sinon son code — un code que le référentiel ne connaît plus reste lisible. */
const reasonName = (r: { code: string; label: string | null }): string => r.label ?? r.code;

const GRID_ROWS: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr) 70px 50px 64px', gap: 10 };

/** L'onglet Remises : six indicateurs, où vont les remises, qui remise quoi, l'évolution. */
export function StatsRemises({ view, lang, motif, onMotif }: {
  view: SalesDiscountsView; lang: string;
  /** Le motif choisi (clic sur sa ligne) : l'évolution ne montre que lui, et sa colonne ressort chez les vendeurs. */
  motif: string; onMotif: (code: string) => void;
}) {
  const refName = view.reference.kind === 'n1' ? t('N-1') : t('Comparaison');
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  const a = view.totals;
  const r = view.refTotals;
  const series = (f: (x: SalesDiscountTotals) => number | null) => view.buckets.map((b) => f(b.totals));
  const ticketsOf = (n: number): string => t('{n} ticket', { n });
  return (
    <>
      <div style={KPI_ROW}>
        <KpiCard label={t('Remises accordées')} icon="tag" tone="violet" value={eur0(a.totalCents)} growth={view.growth.total} inverse
          refLabel={tip(eur0(r.totalCents))} sub={t('sur {montant} de CA brut', { montant: eur0(a.grossTtcCents) })} series={series((x) => x.totalCents)} />
        <KpiCard label={t('Taux de remise')} icon="percent" tone="violet" value={pc(a.rateBp)} growth={view.growth.rate} inverse
          refLabel={tip(pc(r.rateBp))} sub={t('du CA brut, toutes remises')} series={series((x) => x.rateBp)} />
        <KpiCard label={t('Tickets remisés')} icon="receipt" tone="teal" value={ticketsOf(a.discountedTickets)} growth={view.growth.discountedTickets} inverse
          refLabel={tip(ticketsOf(r.discountedTickets))} sub={t('{part} des tickets', { part: pc(a.discountedShareBp) })} series={series((x) => x.discountedTickets)} />
        <KpiCard label={t('Remise moyenne')} icon="basket" tone="teal" value={eur0(a.averageCents)} growth={view.growth.average} inverse
          refLabel={tip(eur0(r.averageCents))} sub={t('par ticket remisé')} series={series((x) => x.averageCents)} />
        <KpiCard label={t('Promotions & soldes')} icon="megaphone" tone="blue" value={eur0(a.promoCents)} growth={view.growth.promo} neutral
          refLabel={tip(eur0(r.promoCents))} sub={t('{part} des remises · automatiques', { part: pc(a.promoShareBp) })} series={series((x) => x.promoCents)} />
        <KpiCard label={t('Sans motif')} icon="alert" tone="amber" value={eur0(a.withoutReasonCents)} growth={view.growth.withoutReason} inverse
          refLabel={tip(eur0(r.withoutReasonCents))} sub={t('{part} des remises', { part: pc(a.withoutReasonShareBp) })} series={series((x) => x.withoutReasonCents)} />
      </div>
      <div style={CARD_ROW}>
        <Breakdown view={view} refName={refName} motif={motif} onMotif={onMotif} />
        <Sellers view={view} motif={motif} />
      </div>
      <EvolutionBars view={view} lang={lang} motif={motif} />
    </>
  );
}

/** OÙ VONT LES REMISES — un motif par ligne (cliquable), puis les parts qui ne sont pas des motifs. */
function Breakdown({ view, refName, motif, onMotif }: { view: SalesDiscountsView; refName: string; motif: string; onMotif: (code: string) => void }) {
  const navigate = useNavigate();
  const { tipProps, tip } = useChartTip();
  const parts = PART_ORDER.filter((k) => ALWAYS.has(k) || view.others[k].cents !== 0 || view.others[k].refCents !== 0);
  const max = Math.max(1, ...view.reasons.map((x) => x.cents), ...parts.map((k) => view.others[k].cents));
  const width = (cents: number) => `${Math.max(1, (cents / max) * 100).toFixed(1)}%`;
  const top = view.reasons[0];
  const insight = view.totals.totalCents === 0 ? t('Aucune remise sur la période.')
    : [top ? t('« {motif} » pèse {part} des remises', { motif: reasonName(top), part: pc(top.shareBp) }) : null,
      t('{part} restent sans motif', { part: pc(view.totals.withoutReasonShareBp) })].filter(Boolean).join(' · ');
  const subOf = (x: SalesDiscountsView['reasons'][number]) => [
    x.lines > 0 ? t('{n} ligne remisée', { n: x.lines }) : null,
    x.tickets > 0 ? t('{n} ticket entier', { n: x.tickets }) : null,
    // ⚠️ UN PRIX LIBRE BAISSÉ EST COMPTÉ, JAMAIS CHIFFRÉ : la caisse n'en scelle pas encore le prix d'origine.
    x.freePriceLines > 0 ? t('{n} prix libre, sans montant', { n: x.freePriceLines }) : null,
  ].filter(Boolean).join(' · ');
  return (
    <div style={{ ...CARD, flex: '1 1 440px', minWidth: 'min(100%,420px)' }}>
      <CardHead icon="tag" tone="violet" title={t('Où vont les remises')} insight={insight}>
        <button type="button" onClick={() => navigate('/referentiels?tab=motifs')} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
          background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
        }}><Icon name="settings2" size={13} stroke={1.9} />{t('Gérer les motifs')}</button>
      </CardHead>
      <div style={{ padding: '4px 10px 12px' }}>
        <div style={{ ...GRID_ROWS, ...MONO_LABEL, padding: '4px 8px 6px' }}>
          <span>{t('Source')}</span><span /><span style={{ textAlign: 'right' }}>{t('Montant')}</span>
          <span style={{ textAlign: 'right' }}>{t('Part')}</span><span style={{ textAlign: 'right' }}>{t('vs {reference}', { reference: refName })}</span>
        </div>
        {view.reasons.map((x, i) => {
          const on = motif === x.code;
          return (
            <button key={x.code} type="button" aria-pressed={on} onClick={() => onMotif(on ? '' : x.code)}
              title={on ? t('Montrer tous les motifs') : t('Ne regarder que ce motif')}
              style={{
                ...GRID_ROWS, alignItems: 'center', width: '100%', padding: 8, borderRadius: 'var(--r-sm)', textAlign: 'left', cursor: 'pointer',
                border: `1px solid ${on ? 'var(--violet)' : 'transparent'}`, background: on ? 'var(--violet-bg)' : 'transparent',
              }}>
              <Origin color={reasonColor(i)} label={reasonName(x)} sub={subOf(x)} />
              <Bar color={reasonColor(i)} width={width(x.cents)} />
              <Amount cents={x.cents} share={x.shareBp} growth={x.growthBp} tipProps={tipProps} tipLabel={reasonName(x)} refValue={`${refName} ${eur0(x.refCents)}`} />
            </button>
          );
        })}
        {parts.map((k) => {
          const p = view.others[k];
          return (
            <div key={k} style={{ ...GRID_ROWS, alignItems: 'center', padding: 8, border: '1px solid transparent' }}>
              <Origin color={PARTS[k].color} label={t(PARTS[k].label)} sub={t(PARTS[k].sub)} />
              <Bar color={PARTS[k].color} width={width(p.cents)} />
              <Amount cents={p.cents} share={p.shareBp} growth={p.growthBp} neutral={PARTS[k].neutral} tipProps={tipProps} tipLabel={t(PARTS[k].label)}
                refValue={`${refName} ${eur0(p.refCents)}`} />
            </div>
          );
        })}
        {tip}
      </div>
    </div>
  );
}

/** Le nom d'une origine : sa pastille de couleur, son libellé, sa phrase. */
function Origin({ color, label, sub }: { color: string; label: string; sub: string }) {
  return (
    <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>}
      </span>
    </span>
  );
}

/** La barre d'une origine, à la largeur de son montant. */
function Bar({ color, width }: { color: string; width: string }) {
  return (
    <span style={{ height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
      <span style={{ display: 'block', height: '100%', width, background: color, borderRadius: 'var(--r-pill)' }} />
    </span>
  );
}

/** Le montant, la part et l'écart d'une origine. Pour une remise, monter n'est pas une bonne nouvelle (rose). */
function Amount({ cents, share, growth, neutral, tipProps, tipLabel, refValue }: {
  cents: number; share: number | null; growth: number | null; neutral?: boolean;
  tipProps: ReturnType<typeof useChartTip>['tipProps']; tipLabel: string; refValue: string;
}) {
  const c = chip(growth, true, neutral);
  return (
    <>
      <strong style={{ textAlign: 'right', fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{eur0(cents)}</strong>
      <span style={{ textAlign: 'right', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{pc(share)}</span>
      <span role="img" {...tipProps({ label: tipLabel, value: dTx(growth), detail: refValue })} style={{
        justifySelf: 'end', display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-pill)', background: c.bg,
        color: c.fg, fontSize: 'var(--t-meta)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>{dTx(growth)}</span>
    </>
  );
}

/** Le nom d'une colonne de « Qui remise quoi ». */
const columnName = (c: SalesDiscountsView['sellers']['columns'][number]): string =>
  c.kind === 'others' ? t('Autres motifs') : c.kind === 'none' ? t('Sans motif') : (c.label ?? c.code ?? '');

/** QUI REMISE QUOI — les gestes de chaque vendeur, par motif ; son taux face à celui du magasin. */
function Sellers({ view, motif }: { view: SalesDiscountsView; motif: string }) {
  const { tipProps, tip } = useChartTip();
  const s = view.sellers;
  const on = s.rows.length > 0 && s.rows.some((x) => x.gesturesCents !== 0);
  const cellMax = Math.max(1, ...s.rows.flatMap((x) => x.cells));
  const flagged = s.rows.filter((x) => x.flagged).sort((x, y) => (y.gapBp ?? 0) - (x.gapBp ?? 0))[0];
  const insight = !s.rows.length ? t('Aucun vendeur sur la période.')
    : flagged ? t('{vendeur} remise {ecart} de plus que la moyenne du magasin', { vendeur: flagged.name, ecart: points(flagged.gapBp!) })
      : t('Les vendeurs remisent tous près de la moyenne ({taux})', { taux: pc(s.storeRateBp) });
  const grid = `minmax(118px,1.3fr) repeat(${s.columns.length}, minmax(56px,1fr)) 72px 62px`;
  return (
    <div style={{ ...CARD, flex: '1.3 1 520px', minWidth: 'min(100%,480px)' }}>
      <CardHead icon="users" tone="teal" title={t('Qui remise quoi')} insight={insight} />
      {on ? (
        <>
          <div style={{ padding: '6px 16px 4px', overflowX: 'auto' }}>
            <div role="table" aria-label={t('Qui remise quoi')} style={{ minWidth: 520, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 5, alignItems: 'end', paddingBottom: 3, ...MONO_LABEL }}>
                <span role="columnheader">{t('Vendeur')}</span>
                {s.columns.map((c, i) => (
                  <span key={c.key} role="columnheader" title={columnName(c)} style={{
                    textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    color: c.code !== null && c.code === motif ? 'var(--violet-fg)' : undefined,
                  }}>
                    <span style={{ display: 'block', width: 8, height: 8, borderRadius: 2, margin: '0 auto 3px', background: columnColor(c, i) }} />
                    {columnName(c)}
                  </span>
                ))}
                <span role="columnheader" style={{ textAlign: 'right' }}>{t('Total')}</span>
                <span role="columnheader" style={{ textAlign: 'right' }}>{t('Taux')}</span>
              </div>
              {s.rows.map((row) => (
                <div key={row.id} role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 5, alignItems: 'center' }}>
                  <span role="cell" style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
                  {row.cells.map((x, j) => {
                    const c = s.columns[j]!;
                    const ratio = x / cellMax;
                    const dim = motif !== '' && c.code !== motif;
                    const base = c.kind === 'none' ? 'var(--amber)' : 'var(--violet)';
                    return (
                      <span key={c.key} role="cell" {...tipProps({ label: `${row.name} · ${columnName(c)}`, value: eur0(x) })} style={{
                        height: 36, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--t-dense)', fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums', opacity: dim ? 0.35 : 1, color: ratio > 0.55 ? 'var(--paper)' : 'var(--ink-800)',
                        background: x > 0 ? `color-mix(in oklab, ${base} ${Math.round(14 + 70 * ratio)}%, var(--paper))` : 'var(--ink-50)',
                      }}>{x > 0 ? axisLabel(x, 'eur') : '—'}</span>
                    );
                  })}
                  <strong role="cell" style={{ textAlign: 'right', fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{eur0(row.gesturesCents)}</strong>
                  <span role="cell" {...tipProps({
                    label: row.name, value: pc(row.rateBp),
                    detail: t('Gestes sur {montant} de CA brut — {ecart} face à la moyenne du magasin', { montant: eur0(row.grossTtcCents), ecart: row.gapBp === null ? '—' : points(row.gapBp) }),
                  })} style={{
                    justifySelf: 'end', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-pill)',
                    background: row.flagged ? TONES.amber[0] : 'var(--ink-100)', color: row.flagged ? TONES.amber[1] : 'var(--ink-700)',
                    fontSize: 'var(--t-dense)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                  }}>{pc(row.rateBp)}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: '9px 16px 13px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
            {t('Taux = gestes du vendeur (remises au comptoir et remises du ticket) ÷ son CA brut · moyenne du magasin {taux} · en ambre au-delà de 1,5 point. Les promotions, la remise permanente des clients et la fidélité n’appartiennent à personne : elles n’y figurent pas.', { taux: pc(s.storeRateBp) })}
          </div>
          {tip}
        </>
      ) : (
        <div style={EMPTY}>{t('Aucune remise manuelle sur la période.')}</div>
      )}
    </div>
  );
}

/** La teinte d'une colonne : celle de son motif, gris pour les autres motifs, ambre pour les gestes sans motif. */
const columnColor = (c: SalesDiscountsView['sellers']['columns'][number], i: number): string =>
  c.kind === 'others' ? 'var(--ink-400)' : c.kind === 'none' ? 'var(--amber)' : reasonColor(i);

/** Un écart en points de pourcentage, signé (« +4,7 pt »). */
const points = (bp: number): string =>
  t('{ecart} pt', { ecart: `${bp > 0 ? '+' : bp < 0 ? '−' : ''}${nombre(pctOf(Math.abs(bp)), { fractionDigits: 1 })}` });

/** L'ÉVOLUTION — les remises par seau, empilées par origine ; ou le seul motif choisi. */
function EvolutionBars({ view, lang, motif }: { view: SalesDiscountsView; lang: string; motif: string }) {
  const { tipProps, tip } = useChartTip();
  const selected = view.reasons.findIndex((x) => x.code === motif);
  const sel = selected >= 0 ? view.reasons[selected]! : null;
  const layers: { label: string; color: string; of: (b: SalesDiscountsView['buckets'][number]) => number }[] = sel
    ? [{ label: reasonName(sel), color: reasonColor(selected), of: (b) => b.byReason[sel.code] ?? 0 }]
    : [
      { label: t('Gestes avec motif'), color: 'var(--violet)', of: (b) => b.totals.withReasonCents },
      ...PART_ORDER.filter((k) => view.others[k].cents !== 0 || ALWAYS.has(k)).map((k) => ({
        label: t(PARTS[k].label), color: PARTS[k].color, of: (b: SalesDiscountsView['buckets'][number]) => b.totals[PART_KEY[k]],
      })),
    ];
  const totals = view.buckets.map((b) => layers.reduce((s, l) => s + Math.max(0, l.of(b)), 0));
  const n = view.buckets.length;
  const top = niceTop(Math.max(0, ...totals));
  const labels = view.buckets.map((b) => bucketLabels(b.key, view.grain, lang));
  let pk = 0;
  totals.forEach((v, i) => { if (v > totals[pk]!) pk = i; });
  const noun = t(GRAIN_NOUN[view.grain]);
  const insight = n ? t('Par {grain} · pic : {quand} — {montant}', { grain: noun, quand: labels[pk]!.tip, montant: eur0(totals[pk]!) })
    : t('Aucun jour ouvré dans la période.');
  const step = Math.max(1, Math.ceil(n / 8));
  return (
    <div style={{ ...CARD, flex: '1 1 100%', minWidth: 'min(100%,320px)' }}>
      <CardHead icon="barChart" tone="blue" title={sel ? t('Évolution — « {motif} »', { motif: reasonName(sel) }) : t('Évolution des remises')} insight={insight} />
      {n > 0 ? (
        <>
          <div style={{ padding: '10px 16px 0' }}>
            <div style={{ position: 'relative', height: 200, marginLeft: 46 }}>
              {[0, 0.5, 1].map((f) => (
                <div key={f}>
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
                  <span style={{
                    position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${f * 100}%`,
                  }}>{axisLabel(top * f, 'eur')}</span>
                </div>
              ))}
              <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: n > 40 ? 2 : n > 20 ? 4 : 7 }}>
                {view.buckets.map((b, i) => {
                  const total = totals[i]!;
                  return (
                    <div key={b.key} role="listitem" {...tipProps({
                      label: labels[i]!.tip, value: eur0(total),
                      detail: layers.length > 1 ? layers.map((l) => `${l.label} ${eur0(l.of(b))}`).join(' · ') : undefined,
                    })} style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{
                        width: '100%', height: `${Math.max(total > 0 ? 0.5 : 0, (total / top) * 100).toFixed(2)}%`, display: 'flex', flexDirection: 'column-reverse',
                        borderRadius: '4px 4px 0 0', overflow: 'hidden',
                      }}>
                        {layers.map((l) => {
                          const v = Math.max(0, l.of(b));
                          return v > 0 ? <div key={l.label} style={{ height: `${((v / total) * 100).toFixed(2)}%`, background: l.color }} /> : null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {tip}
            </div>
            <div style={{ position: 'relative', height: 24, marginLeft: 46 }}>
              {labels.map((l, i) => (i % step === 0 ? (
                <span key={view.buckets[i]!.key} style={{
                  position: 'absolute', top: 7, left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)',
                  color: 'var(--ink-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                }}>{l.axis}</span>
              ) : null))}
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', padding: '9px 16px 13px', marginTop: 4,
            borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-600)',
          }}>
            {layers.map((l) => (
              <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color }} />{l.label}
              </span>
            ))}
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucun jour ouvré dans la période choisie — élargissez les dates.')}</div>
      )}
    </div>
  );
}

/** Le total d'une part dans les totaux d'un seau. */
const PART_KEY: Record<SalesDiscountPart, 'clientCents' | 'loyaltyCents' | 'promoCents' | 'withoutReasonCents' | 'undetailedCents'> = {
  client: 'clientCents', loyalty: 'loyaltyCents', promo: 'promoCents', withoutReason: 'withoutReasonCents', undetailed: 'undetailedCents',
};
const GRAIN_NOUN: Record<SalesDiscountsView['grain'], Translatable> = { day: key('jour'), week: key('semaine'), month: key('mois') };

/** Le gabarit de la première lecture : les six tuiles à leur place, sans un chiffre. */
export function StatsRemisesPlaceholder() {
  const tiles: { label: Translatable; icon: 'tag' | 'percent' | 'receipt' | 'basket' | 'megaphone' | 'alert'; tone: 'violet' | 'teal' | 'blue' | 'amber' }[] = [
    { label: key('Remises accordées'), icon: 'tag', tone: 'violet' }, { label: key('Taux de remise'), icon: 'percent', tone: 'violet' },
    { label: key('Tickets remisés'), icon: 'receipt', tone: 'teal' }, { label: key('Remise moyenne'), icon: 'basket', tone: 'teal' },
    { label: key('Promotions & soldes'), icon: 'megaphone', tone: 'blue' }, { label: key('Sans motif'), icon: 'alert', tone: 'amber' },
  ];
  return (
    <div aria-busy="true" style={KPI_ROW}>
      {tiles.map((x) => (
        <div key={x.icon} style={{ ...KPI_TILE, paddingBottom: 13 }}>
          <KpiLabel label={t(x.label)} icon={x.icon} tone={x.tone} />
          <div style={{ marginTop: 10, height: 22, width: '60%', borderRadius: 6, background: 'var(--ink-100)' }} />
        </div>
      ))}
    </div>
  );
}

/** La pastille du motif choisi, dans la barre des filtres — sa croix montre de nouveau tous les motifs. */
export function MotifChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 32, borderRadius: 'var(--r-pill)', maxWidth: 320,
      background: 'var(--violet-bg)', border: '1px solid var(--violet)', color: 'var(--violet-fg)',
    }}>
      <span style={{ padding: '0 4px 0 12px', fontSize: 'var(--t-dense)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <Trans k='Motif : <1>{motif}</1>' vars={{ motif: label }} parts={[(x) => <strong>{x}</strong>]} />
      </span>
      <button type="button" onClick={onClear} aria-label={t('Montrer tous les motifs')} title={t('Montrer tous les motifs')} style={{
        width: 26, height: '100%', flexShrink: 0, border: 'none', borderRadius: '0 var(--r-pill) var(--r-pill) 0', background: 'transparent',
        color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}><Icon name="x" size={11} stroke={2.6} /></button>
    </span>
  );
}
